// Profile tab editing (B6) — the executed Loan terms card. Read view leads
// with the "From Final Financial Addendum" provenance (plus "Last edited
// by {name}, {date}" once corrected); a quiet per-section Edit button
// (Loans.Manage) flips the card into a form. Saving requires a reason —
// these are executed contract terms, so the confirm modal audits
// before/after and notes that true amendments (rate changes, extensions)
// become lifecycle events later; this edit is for corrections.

import { useState } from "react";
import { Button, Card } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { useUpdateLoanTerms } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { formatCurrency } from "@/components/deals/dealFormat";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import { calendarDate, formatDate } from "../common/colorBand";

const PAYMENT_FREQUENCY_LABEL: Record<number, string> = {
  1: "Monthly",
};

const NOTE_FEE_HELPER =
  "Leave blank if the note has no such clause — automatic assessment stays off.";

interface TermsDraft {
  originalLoanAmount: string;
  interestRatePct: string;
  termMonths: string;
  amortizationMonths: string;
  maturityDate: string;
  firstPaymentDate: string;
  paymentDay: string;
  paymentFrequency: string;
  // Note fee terms — blank means the note has no such clause (saved as
  // null; automatic assessment stays off). Percent fields edit as percent
  // and save as fractions, same as interestRatePct.
  lateFeePct: string;
  lateFeeGraceDays: string;
  nsfFeeAmount: string;
  defaultInterestRatePct: string;
  prepaymentFeePct: string;
}

function hydrate(loan: LoanSummaryDto): TermsDraft {
  return {
    originalLoanAmount: String(loan.originalLoanAmount),
    interestRatePct: (loan.interestRate * 100).toFixed(2),
    termMonths: String(loan.termMonths),
    amortizationMonths: String(loan.amortizationMonths),
    maturityDate: loan.maturityDate.slice(0, 10),
    firstPaymentDate: loan.firstPaymentDate.slice(0, 10),
    paymentDay: String(loan.paymentDay),
    paymentFrequency: String(loan.paymentFrequency),
    lateFeePct: loan.lateFeePct != null ? (loan.lateFeePct * 100).toFixed(2) : "",
    lateFeeGraceDays: loan.lateFeeGraceDays != null ? String(loan.lateFeeGraceDays) : "",
    nsfFeeAmount: loan.nsfFeeAmount != null ? String(loan.nsfFeeAmount) : "",
    defaultInterestRatePct:
      loan.defaultInterestRatePct != null
        ? (loan.defaultInterestRatePct * 100).toFixed(2)
        : "",
    prepaymentFeePct:
      loan.prepaymentFeePct != null ? (loan.prepaymentFeePct * 100).toFixed(2) : "",
  };
}

interface Parsed {
  originalLoanAmount: number;
  interestRate: number;
  termMonths: number;
  amortizationMonths: number;
  maturityDate: string;
  firstPaymentDate: string;
  paymentDay: number;
  paymentFrequency: number;
  lateFeePct: number | null;
  lateFeeGraceDays: number | null;
  nsfFeeAmount: number | null;
  defaultInterestRatePct: number | null;
  prepaymentFeePct: number | null;
}

/** Optional numeric input: blank → null; unparseable → undefined. */
function parseOptionalNumber(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed.replace(/[$,%]/g, ""));
  return Number.isFinite(n) ? n : undefined;
}

/** Blocking validation; returns the parsed payload or a message. */
function parseDraft(d: TermsDraft): { parsed?: Parsed; error?: string } {
  const amount = Number(d.originalLoanAmount.replace(/[$,]/g, ""));
  const ratePct = Number(d.interestRatePct);
  const term = Number(d.termMonths);
  const amort = Number(d.amortizationMonths);
  const paymentDay = Number(d.paymentDay);
  const paymentFrequency = Number(d.paymentFrequency);
  if (!Number.isFinite(amount) || amount <= 0)
    return { error: "Original loan amount must be greater than zero." };
  if (!Number.isFinite(ratePct) || ratePct < 0 || ratePct > 30)
    return { error: "Interest rate must be between 0% and 30%." };
  if (!Number.isInteger(term) || term <= 0)
    return { error: "Term must be a positive number of months." };
  if (!Number.isInteger(amort) || amort <= 0)
    return { error: "Amortization must be a positive number of months." };
  if (!d.maturityDate || !d.firstPaymentDate)
    return { error: "Maturity and first payment dates are required." };
  if (d.maturityDate <= d.firstPaymentDate)
    return { error: "Maturity date must be after the first payment date." };
  if (!Number.isInteger(paymentDay) || paymentDay < 1 || paymentDay > 28)
    return { error: "Payment day must be between 1 and 28." };
  if (!Number.isInteger(paymentFrequency) || paymentFrequency < 1)
    return { error: "Payment frequency must be at least 1." };

  // Note fee terms — optional/clearable; percent inputs save as fractions.
  const lateFeePct = parseOptionalNumber(d.lateFeePct);
  if (lateFeePct === undefined || (lateFeePct !== null && (lateFeePct < 0 || lateFeePct >= 100)))
    return { error: "Late fee must be between 0% and 100%, or blank." };
  const graceDays = parseOptionalNumber(d.lateFeeGraceDays);
  if (graceDays === undefined || (graceDays !== null && (!Number.isInteger(graceDays) || graceDays < 0)))
    return { error: "Late fee grace days must be a whole number of days, or blank." };
  const nsfFee = parseOptionalNumber(d.nsfFeeAmount);
  if (nsfFee === undefined || (nsfFee !== null && nsfFee < 0))
    return { error: "NSF fee cannot be negative." };
  const defaultRatePct = parseOptionalNumber(d.defaultInterestRatePct);
  if (
    defaultRatePct === undefined ||
    (defaultRatePct !== null && (defaultRatePct <= 0 || defaultRatePct >= 100))
  )
    return { error: "Default interest rate must be greater than 0% and less than 100%, or blank." };
  const prepayPct = parseOptionalNumber(d.prepaymentFeePct);
  if (prepayPct === undefined || (prepayPct !== null && (prepayPct < 0 || prepayPct >= 100)))
    return { error: "Prepayment fee must be between 0% and 100%, or blank." };

  return {
    parsed: {
      originalLoanAmount: amount,
      interestRate: ratePct / 100,
      termMonths: term,
      amortizationMonths: amort,
      maturityDate: `${d.maturityDate}T00:00:00Z`,
      firstPaymentDate: `${d.firstPaymentDate}T00:00:00Z`,
      paymentDay,
      paymentFrequency,
      lateFeePct: lateFeePct === null ? null : lateFeePct / 100,
      lateFeeGraceDays: graceDays,
      nsfFeeAmount: nsfFee,
      defaultInterestRatePct: defaultRatePct === null ? null : defaultRatePct / 100,
      prepaymentFeePct: prepayPct === null ? null : prepayPct / 100,
    },
  };
}

/** Warn-not-block heads-up for unusual (but permitted) values. */
function unusualValueWarning(d: TermsDraft): string | null {
  const ratePct = Number(d.interestRatePct);
  const amount = Number(d.originalLoanAmount.replace(/[$,]/g, ""));
  const term = Number(d.termMonths);
  const warnings: string[] = [];
  if (Number.isFinite(ratePct) && ratePct > 15)
    warnings.push(`an interest rate of ${ratePct.toFixed(2)}%`);
  if (Number.isFinite(amount) && amount > 25_000_000)
    warnings.push(`a loan amount of ${formatCurrency(amount)}`);
  if (Number.isInteger(term) && term > 360) warnings.push(`a ${term}-month term`);
  if (warnings.length === 0) return null;
  return `Unusual value: ${warnings.join(", ")}. Double-check before saving.`;
}

export function LoanTermsCard({ loan }: { loan: LoanSummaryDto }) {
  const auth = useAuth();
  const canEdit = auth.hasPermission("Loans.Manage") || auth.hasRoleCode("ADMIN");
  const toast = useToast();
  const update = useUpdateLoanTerms(loan.id);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<TermsDraft>(() => hydrate(loan));
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState("");

  const startEdit = () => {
    setDraft(hydrate(loan));
    setError(null);
    setEditing(true);
  };

  const requestSave = () => {
    setError(null);
    // FIND-12: open-edit-save-unchanged is a zero-change no-op.
    const initial = hydrate(loan);
    if ((Object.keys(initial) as (keyof TermsDraft)[]).every((k) => initial[k] === draft[k])) {
      setEditing(false);
      return;
    }
    const { error: validationError } = parseDraft(draft);
    if (validationError) {
      setError(validationError);
      return;
    }
    setReason("");
    setConfirmOpen(true);
  };

  const confirmSave = async () => {
    const { parsed } = parseDraft(draft);
    if (!parsed || reason.trim().length === 0) return;
    try {
      await update.mutateAsync({ ...parsed, reason: reason.trim() });
      toast.success("Loan terms updated");
      setConfirmOpen(false);
      setEditing(false);
    } catch (err) {
      setError(extractErrorMessage(err));
      setConfirmOpen(false);
    }
  };

  const warning = editing ? unusualValueWarning(draft) : null;

  const caption = (
    <>
      From Final Financial Addendum
      {loan.termsEditedAt && (
        <span data-testid="loan-terms-last-edited">
          {" "}
          · Last edited by {loan.termsEditedByName ?? "Unknown user"},{" "}
          {formatDate(loan.termsEditedAt)}
        </span>
      )}
    </>
  );

  const inputClass =
    "mt-1 w-full border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure";

  return (
    <Card data-testid="loan-terms-card">
      <div className="mb-space-3 flex items-start justify-between gap-space-3">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">Loan terms</div>
          <p className="text-caption text-gray-mid m-0">{caption}</p>
        </div>
        {canEdit && !editing && (
          <Button
            variant="tertiary"
            size="sm"
            onClick={startEdit}
            data-testid="loan-terms-edit"
          >
            Edit
          </Button>
        )}
      </div>

      {!editing ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 text-small">
            <Value label="Original loan amount">
              <span className="font-mono tabular-nums">
                {formatCurrency(loan.originalLoanAmount)}
              </span>
            </Value>
            <Value label="Interest rate">
              <span className="font-mono tabular-nums">{`${(loan.interestRate * 100).toFixed(2)}%`}</span>
            </Value>
            <Value label="Term">{`${loan.termMonths} months`}</Value>
            <Value label="Amortization">{`${loan.amortizationMonths} months`}</Value>
            <Value label="Maturity date">{calendarDate(loan.maturityDate)}</Value>
            <Value label="First payment date">{calendarDate(loan.firstPaymentDate)}</Value>
            <Value label="Payment day">
              {loan.usesBusinessDayScheduleRule
                ? "First business day of each month"
                : `Day ${loan.paymentDay}`}
            </Value>
            <Value label="Payment frequency">
              {PAYMENT_FREQUENCY_LABEL[loan.paymentFrequency] ??
                `Every ${loan.paymentFrequency} month(s)`}
            </Value>
          </div>
          <div
            className="mt-space-4 pt-space-3 border-t border-border-light"
            data-testid="loan-note-fee-terms"
          >
            <div className="font-display font-bold text-small text-onyx mb-space-2">
              Note fee terms
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 text-small">
              <Value label="Late fee">
                {loan.lateFeePct != null ? (
                  <span className="font-mono tabular-nums">{`${(loan.lateFeePct * 100).toFixed(2)}%`}</span>
                ) : (
                  "—"
                )}
              </Value>
              <Value label="Late fee grace period">
                {loan.lateFeeGraceDays != null ? `${loan.lateFeeGraceDays} days` : "—"}
              </Value>
              <Value label="NSF fee">
                {loan.nsfFeeAmount != null ? (
                  <span className="font-mono tabular-nums">
                    {formatCurrency(loan.nsfFeeAmount)}
                  </span>
                ) : (
                  "—"
                )}
              </Value>
              <Value label="Default interest rate">
                {loan.defaultInterestRatePct != null ? (
                  <span className="font-mono tabular-nums">{`${(loan.defaultInterestRatePct * 100).toFixed(2)}%`}</span>
                ) : (
                  "—"
                )}
              </Value>
              <Value label="Prepayment fee">
                {loan.prepaymentFeePct != null ? (
                  <span className="font-mono tabular-nums">{`${(loan.prepaymentFeePct * 100).toFixed(2)}%`}</span>
                ) : (
                  "—"
                )}
              </Value>
            </div>
            <p className="text-caption text-gray-mid mt-space-2 m-0">
              “—” means the note has no such clause — automatic assessment stays off.
            </p>
          </div>
        </>
      ) : (
        <div data-testid="loan-terms-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 text-small">
            <Field id="terms-amount" label="Original loan amount">
              <input
                id="terms-amount"
                type="text"
                inputMode="decimal"
                className={inputClass}
                value={draft.originalLoanAmount}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, originalLoanAmount: e.target.value }))
                }
              />
            </Field>
            <Field id="terms-rate" label="Interest rate (%)">
              <input
                id="terms-rate"
                type="text"
                inputMode="decimal"
                className={inputClass}
                value={draft.interestRatePct}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, interestRatePct: e.target.value }))
                }
              />
            </Field>
            <Field id="terms-term" label="Term (months)">
              <input
                id="terms-term"
                type="text"
                inputMode="numeric"
                className={inputClass}
                value={draft.termMonths}
                onChange={(e) => setDraft((d) => ({ ...d, termMonths: e.target.value }))}
              />
            </Field>
            <Field id="terms-amort" label="Amortization (months)">
              <input
                id="terms-amort"
                type="text"
                inputMode="numeric"
                className={inputClass}
                value={draft.amortizationMonths}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, amortizationMonths: e.target.value }))
                }
              />
            </Field>
            <Field id="terms-maturity" label="Maturity date">
              <input
                id="terms-maturity"
                type="date"
                className={inputClass}
                value={draft.maturityDate}
                onChange={(e) => setDraft((d) => ({ ...d, maturityDate: e.target.value }))}
              />
            </Field>
            <Field id="terms-first-payment" label="First payment date">
              <input
                id="terms-first-payment"
                type="date"
                className={inputClass}
                value={draft.firstPaymentDate}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, firstPaymentDate: e.target.value }))
                }
              />
            </Field>
            {loan.usesBusinessDayScheduleRule ? (
              // Business-day rule (§2.4): the recurrence is derived (first
              // business day of each month), not a stored day-of-month, so the
              // field is read-only. The prior loan.paymentDay round-trips
              // unchanged in the payload (it's inert for these loans).
              <Field id="terms-payment-day" label="Payment day">
                <input
                  id="terms-payment-day"
                  type="text"
                  className={inputClass}
                  value="First business day of each month"
                  disabled
                  readOnly
                />
              </Field>
            ) : (
              <Field id="terms-payment-day" label="Payment day (1–28)">
                <input
                  id="terms-payment-day"
                  type="text"
                  inputMode="numeric"
                  className={inputClass}
                  value={draft.paymentDay}
                  onChange={(e) => setDraft((d) => ({ ...d, paymentDay: e.target.value }))}
                />
              </Field>
            )}
            <Field id="terms-frequency" label="Payment frequency (months)">
              <input
                id="terms-frequency"
                type="text"
                inputMode="numeric"
                className={inputClass}
                value={draft.paymentFrequency}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, paymentFrequency: e.target.value }))
                }
              />
            </Field>
          </div>

          <div className="mt-space-4 pt-space-3 border-t border-border-light">
            <div className="font-display font-bold text-small text-onyx">
              Note fee terms
            </div>
            <p className="text-caption text-gray-mid mt-1 mb-space-2">
              {NOTE_FEE_HELPER}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 text-small">
              <Field id="terms-late-fee" label="Late fee (%)">
                <input
                  id="terms-late-fee"
                  type="text"
                  inputMode="decimal"
                  className={inputClass}
                  value={draft.lateFeePct}
                  onChange={(e) => setDraft((d) => ({ ...d, lateFeePct: e.target.value }))}
                />
              </Field>
              <Field id="terms-late-fee-grace" label="Late fee grace period (days)">
                <input
                  id="terms-late-fee-grace"
                  type="text"
                  inputMode="numeric"
                  className={inputClass}
                  value={draft.lateFeeGraceDays}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, lateFeeGraceDays: e.target.value }))
                  }
                />
              </Field>
              <Field id="terms-nsf-fee" label="NSF fee ($)">
                <input
                  id="terms-nsf-fee"
                  type="text"
                  inputMode="decimal"
                  className={inputClass}
                  value={draft.nsfFeeAmount}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, nsfFeeAmount: e.target.value }))
                  }
                />
              </Field>
              <Field id="terms-default-interest" label="Default interest rate (%)">
                <input
                  id="terms-default-interest"
                  type="text"
                  inputMode="decimal"
                  className={inputClass}
                  value={draft.defaultInterestRatePct}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, defaultInterestRatePct: e.target.value }))
                  }
                />
              </Field>
              <Field id="terms-prepayment-fee" label="Prepayment fee (%)">
                <input
                  id="terms-prepayment-fee"
                  type="text"
                  inputMode="decimal"
                  className={inputClass}
                  value={draft.prepaymentFeePct}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, prepaymentFeePct: e.target.value }))
                  }
                />
              </Field>
            </div>
          </div>

          {warning && (
            <p
              className="text-caption text-status-warning mt-space-3 m-0"
              data-testid="loan-terms-warning"
            >
              {warning}
            </p>
          )}
          {error && (
            <p
              className="text-caption text-status-error mt-space-3 m-0"
              role="alert"
              data-testid="loan-terms-error"
            >
              {error}
            </p>
          )}

          <div className="mt-space-3 flex justify-end gap-space-2">
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={requestSave}
              data-testid="loan-terms-save"
            >
              Save
            </Button>
          </div>
        </div>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirm loan terms correction"
        description="These are executed contract terms. True amendments (rate changes, extensions) will become lifecycle events later — this edit is for corrections only."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void confirmSave()}
              disabled={reason.trim().length === 0}
              loading={update.isPending}
              data-testid="loan-terms-confirm"
            >
              Save correction
            </Button>
          </>
        }
      >
        <label
          htmlFor="terms-reason"
          className="font-display text-caption uppercase tracking-label text-gray-mid"
        >
          Reason for this correction (required)
        </label>
        <textarea
          id="terms-reason"
          className="mt-1 w-full min-h-[88px] border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="loan-terms-reason"
        />
      </Modal>
    </Card>
  );
}

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="font-display text-caption uppercase tracking-label text-gray-mid"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Value({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-display text-caption uppercase tracking-label text-gray-mid">
        {label}
      </div>
      <div className="text-onyx mt-[2px] whitespace-pre-wrap">{children}</div>
    </div>
  );
}
